import { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import SortByList from '../../../../components/PageHelpers/Sort'
import SummariesBtn from '../../../../components/PageHelpers/SummariesBtn'
import UseTranslation from '../../../../hooks/UseTranslation'
import TitleWithCountV2 from '../../../../components/PageHelpers/TitleWithCountV2'
import UsersList from '../../../../images/UsersList.svg'
import ActiveFiltersWithKey from '../../../../components/PageHelpers/ActiveFiltersWithKey'
import FilterByForm from '../../../../components/PageHelpers/FilterByForm'
import OfoqMultiAutoComplete from '../../../../components/Forms/OfoqMultiAutoComplete'
import IconBtnV2 from '../../../../components/Buttons/IconBtnV2'
import FilterAltOutlinedIcon from '../../../../images/filter.svg'
import AppRoutes from '../../../../services/AppRoutes'
import OfoqThemeBtn from '../../../../components/Buttons/OfoqThemeBtn'
import { filterSystemAdminUsers, retrieveSystemAdminUsers } from '../../../../redux/features/users/SystemAdminUsers/SystemAdminUsersThunk'
import { selectSystemAdminUsers } from '../../../../redux/features/users/SystemAdminUsers/SystemAdminUsersSlice'
import OfoqAutoComplete from '../../../../components/Forms/OfoqAutoComplete'
import { normalizeToArray } from '../../../../utils/helpers'

const Desktop = () => {
  const { t } = UseTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const systemAdminUsers = useSelector(selectSystemAdminUsers)
  const [openModal, setOpenModal] = useState(false)
  const [userTypes, setUserTypes] = useState(systemAdminUsers?.activeFilters?.userTypes || [])
  const [companies, setCompanies] = useState(systemAdminUsers?.activeFilters?.companies || [])
  const [subscriptionReminder, setSubscriptionReminder] = useState(
    () => normalizeToArray(systemAdminUsers?.activeFilters?.subscriptionReminder),
  )
  const baseUri = process.env.REACT_APP_ROOT_ROUTE || ''
  const handleNavigation = () => {
    history.push(`${baseUri}${AppRoutes.adduser}`)
  }

  const isFilteredArraysNotEmpty = Object.values(systemAdminUsers?.activeFilters || {})
    .some((singleFilter) => singleFilter?.length)

  useEffect(() => {
    if (systemAdminUsers?.activeFilters?.userTypes?.length !== userTypes.length) {
      setUserTypes(systemAdminUsers?.activeFilters?.userTypes || [])
    }

    if (systemAdminUsers?.activeFilters?.companies?.length !== companies.length) {
      setCompanies(systemAdminUsers?.activeFilters?.companies || [])
    }
    const nextReminder = normalizeToArray(
      systemAdminUsers?.activeFilters?.subscriptionReminder,
    )
    if (nextReminder.length !== subscriptionReminder.length) {
      setSubscriptionReminder(nextReminder)
    }
  }, [systemAdminUsers?.activeFilters])

  useEffect(() => {
    if (!openModal) return

    dispatch(filterSystemAdminUsers({
      queries: {
        userTypes,
        companies,
        subscriptionReminder,
      },
    }))
  }, [userTypes, companies, subscriptionReminder])

  return (
    <Box sx={styles.largeStyle}>
      <Box Box sx={styles.optionsBox}>
        <SummariesBtn
          actionToDispatch={retrieveSystemAdminUsers}
          store={systemAdminUsers}
          sendQueryAsObject
        />
        <Box sx={styles.sortAddBox}>
          <SortByList
            actionToDispatch={retrieveSystemAdminUsers}
            store={systemAdminUsers}
            sendQueryAsObject
            actionProps={{ pageNumber: 0 }}
          />
          <OfoqThemeBtn name={t?.['Add Customer User']} onClick={handleNavigation} customStyle={styles.addCustomerUserBtn} />
        </Box>
      </Box>
      <Box sx={styles.filterBox}>
        <TitleWithCountV2
          icon={<img src={UsersList} alt="UsersList" />}
          iconSize='xl'
          store={systemAdminUsers}
          title={t?.Users}
          customCount={t?.Users}
          withCount
          customStyle={styles.titleHeader}
        />

        <IconBtnV2
          icon={openModal ? <CloseIcon /> : <img src={FilterAltOutlinedIcon} alt="filter icon" />}
          onClick={() => setOpenModal(!openModal)}
          customStyle={styles.filterButton}
        />
      </Box>

      <FilterByForm formStatus={openModal} customStyles={styles.filterForm}>
        <OfoqMultiAutoComplete
          name={t?.['User Type']}
          store={systemAdminUsers?.filterList?.userTypes}
          defaultOption={userTypes}
          refState={setUserTypes}
          customStyle={styles.firstFilter}
        />
        <OfoqMultiAutoComplete
          name={t?.Company}
          store={systemAdminUsers?.filterList?.companies}
          defaultOption={companies}
          refState={setCompanies}
          customStyle={styles.firstFilter}
        />
        <OfoqAutoComplete
          name={t?.['Reminder Message']}
          optionsConfig={systemAdminUsers?.filterList?.subscriptionReminder}
          defaultOption={subscriptionReminder[0] ?? null}
          refState={(selected) => setSubscriptionReminder(normalizeToArray(selected))}
          customStyle={styles.subscriptionReminderFilter}
        />
      </FilterByForm>

      {isFilteredArraysNotEmpty && (
      <ActiveFiltersWithKey
        store={systemAdminUsers}
        actionToDispatch={filterSystemAdminUsers}
      />
      )}
      </Box>
  )
}

const styles = {

  largeStyle: {
    width: '100%',
    display: { xs: 'none', sm: 'block' },
  },
  optionsBox: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    flexWrap: { xs: 'wrap', md: 'nowrap' },
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginTop: '10px',
  },
  sortAddBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    justifyContent: { xs: 'space-between', sm: 'flex-end' },
  },
  filterBox: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
  },
  filterButton: {
    height: '40px',
    width: '40px',
    borderRadius: '12px',
    border: '1px solid #e2e8ee',
    backgroundColor: 'white',
    color: 'text.hint',
    overflow: 'hidden',
  },
  filterForm: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    padding: { sm: '20px 36px 0px', md: '20px 45px', lg: '20px 0px' },
    alignItems: 'flex-start',
    marginTop: { xs: '0px', sm: '0px !important' },
    gap: '10px',
  },
  firstFilter: {
    marginInline: { xs: '0px', md: '10px' },
    marginBottom: '0px',
  },
  titleHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginTop: '10px',
    marginBottom: '10px !important',
    gap: { xs: '5px', sm: '10px' },
    '& img': {
      paddingRight: '5px',
    },
    '& span': {
      fontFamily: 'Avenir 85 light',
      fontWeight: 400,
      fontSize: { xs: '16px', sm: '24px' },
      lineHeight: { xs: '20.15px', sm: '37.08px' },
    },
    '& p': {
      margin: '0',
    },
  },
  addCustomerUserBtn: {
    marginRight: '0px !important',
  },
  subscriptionReminderFilter: {
    marginInline: { xs: '0px', md: '10px' },
    marginBottom: '0px',
    '& label::before': {
      content: "'🔔'",
      fontSize: '11px',
      marginRight: 1,
    },
  },
}

export default Desktop
